import { Injectable } from '@angular/core';
import { ApiServiceService } from './api-service.service';
import { Observable } from 'rxjs';
import { DefaultResponse } from '../pages/base.interface';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { take, tap } from 'rxjs/operators';
import { HttpParamsMapper } from '../shared/mappers/http-params.mapper';
import { EIndicationDocumentStatus } from '../_pages/private/indication/enums/indication.enum';
import {
  AccountIndicationStatisticOutputDTO,
  IndicationScheduleWithDocuments,
} from '../_pages/private/indication/contracts/indication';

export type SearchAccountIndicationsOutputDTO<Rows = any> = {
  rows: Rows[];
  total: number;
  page: number;
  perPage: number;
};

@Injectable({
  providedIn: 'root',
})
export class IndicationsService {
  constructor(private apiService: ApiServiceService, private http: HttpClient) {}

  public cancelScheduleV2(indicationId: number): Observable<void> {
    return this.http.delete<void>(`${environment.api_v2}/v2/indication/${indicationId}`);
  }

  public countIndicationV2(): Observable<boolean> {
    return this.http.get<boolean>(`${environment.api_v2}/v2/indication-info/available`);
  }

  public allowReschedule(id: number): Observable<{ allow: boolean; remaining: number }> {
    return this.http
      .get<{
        allow: boolean;
        remaining: number;
      }>(`${environment.api_v2}/v2/indication-info/${id}/allow-reschedule`)
      .pipe(take(1));
  }

  public getAccountIndications(filters: {
    type: 'schedule' | 'attendance' | 'archive';
    patientId?: number;
    status?: string | string[];
    page?: number;
    perPage?: number;
    patientName?: string;
    documentStatus?: EIndicationDocumentStatus;
    isDelayed?: boolean;
    startDate?: string;
    endDate?: string;
  }): Observable<SearchAccountIndicationsOutputDTO<any>> {
    const params = HttpParamsMapper.toHttpParams({
      ...filters,
      page: filters.page ?? 1,
      perPage: filters.perPage ?? 10,
    });

    return this.http.get<SearchAccountIndicationsOutputDTO<any>>(
      `${environment.api_v2}/v2/indications`,
      { params }
    );
  }

  public getIndicationSchedules(indicationId: number): Observable<IndicationScheduleWithDocuments[]> {
    return this.http.get<IndicationScheduleWithDocuments[]>(
      `${environment.api_v2}/v2/indications/${indicationId}/schedules`
    );
  }

  public getAccountIndicationStatistics(): Observable<AccountIndicationStatisticOutputDTO> {
    return this.http.get<AccountIndicationStatisticOutputDTO>(
      `${environment.api_v2}/v2/indications/indication-statistics`
    );
  }

  public getAccountIndicationStatisticsByPeriod(filters?: {
    status?: string | string[];
    startDate?: string;
    endDate?: string;
  }): Observable<AccountIndicationStatisticOutputDTO> {
    const params = HttpParamsMapper.toHttpParams({
      status: filters?.status,
      startDate: filters?.startDate,
      endDate: filters?.endDate,
    });

    return this.http.get<AccountIndicationStatisticOutputDTO>(
      `${environment.api_v2}/v2/indications/indication-statistics`,
      { params }
    );
  }

  public getById(indicationId: number): Observable<DefaultResponse<any>> {
    return this.apiService.get(`/indications/${indicationId}`).pipe(take(1));
  }

  public save(indication: any): Observable<DefaultResponse<any>> {
    return this.apiService.post(`/indications`, indication);
  }

  public uploadFiles(formData: FormData, indicationId: number): Observable<DefaultResponse<any>> {
    console.log('formData', formData);

    return this.apiService.post(`/indications/${indicationId}/upload`, formData);
  }

  public generateSSOLink(patientId: number): Observable<{ url: string }> {
    return this.apiService
      .post(`/indications/communicare/generate-sso-link/${patientId}`)
      .pipe(tap((sso) => console.log('Gerando SSO', sso)));
  }

  public cancelScheduleOnCommunicare(
    indicationId: number,
    scheduleId: number
  ): Observable<boolean> {
    return this.apiService.delete(
      `/indications/communicare/cancel-schedule/${indicationId}/${scheduleId}`
    );
  }

  public checkAvailabilityForScheduling(patientId: number): Observable<boolean> {
    return this.apiService.get(`/indications/check-availability/${patientId}`);
  }

  public countIndicationsByAccount(): Observable<number> {
    return this.apiService.get(`/indications/count/by-account`);
  }

  public getLastIndicationByPatient(patientId: number): Observable<any> {
    return this.apiService.get(`/indications/last-indication-by-patient-id/${patientId}`);
  }

  public toggleArchiveStatus(indicationId: number): Observable<boolean> {
    return this.apiService.put(
      `${environment.api_v2}/v2/indication/${indicationId}/toggle-archive-status`
    );
  }

  public updateDoctors() {
    return this.apiService.get('/indications/update-doctors');
  }

  public createOrUpdateSchedule(schedule: any): Observable<any> {
    return this.apiService.post(`/schedule`, schedule);
  }

  public getScheduleByIndicationId(indicationId: number): Observable<any> {
    return this.apiService.get(`/schedule/${indicationId}`);
  }

  deleteFile(fileName: string) {
    return this.apiService.delete(`/files/inactivateFile/${fileName}`);
  }

  public validateStatus(indicationId: number): Observable<boolean> {
    return this.apiService.get(`/indications/validate-status/${indicationId}`);
  }
}
